import React, { useState } from 'react';
import { Layout, Menu, Button, Select, Avatar, Dropdown, Space, Tag } from 'antd';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { ThunderboltOutlined, HistoryOutlined, LogoutOutlined, GlobalOutlined, UserOutlined, SettingOutlined, SearchOutlined, ShoppingOutlined, ExportOutlined, TeamOutlined, BankOutlined, UserAddOutlined, DollarOutlined, WalletOutlined, UnorderedListOutlined, DashboardOutlined, FileTextOutlined, ApiOutlined, RollbackOutlined } from '@ant-design/icons';
import { useLanguage } from './contexts/LanguageContext';
import { SessionTimeout } from './components/SessionTimeout';
import { PageCacheProvider } from './contexts/PageCacheContext';
import { userApi, testApi } from './services/api';
import TestExecutor from './pages/TestExecutor';
import ExecutionHistory from './pages/ExecutionHistory';
import AccountQuery from './pages/AccountQuery';
import OrderQuery from './pages/OrderQuery';
import WithdrawQuery from './pages/WithdrawQuery';
import BankCardBind from './pages/BankCardBind';
import AccountCreate from './pages/AccountCreate';
import Payin from './pages/Payin';
import Payout from './pages/Payout';
import Refund from './pages/Refund';
import RefundQuery from './pages/RefundQuery';
import OrderList from './pages/OrderList';
import BankAccountQuery from './pages/BankAccountQuery';
import UserManagement from './pages/UserManagement';
import ChannelAccountManagement from './pages/ChannelAccountManagement';
import UserProfile from './pages/UserProfile';
import ApiDocumentation from './pages/ApiDocumentation';
import MockRuleManagement from './pages/MockRuleManagement';
import Login from './pages/LoginEnhanced';
import Dashboard from './pages/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import { getEnvByHostname } from './utils/env';
import './App.css';

const { Header, Content, Sider } = Layout;

function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, changeLanguage } = useLanguage();
  const username = localStorage.getItem('username');
  const userRole = localStorage.getItem('userRole'); // 获取用户角色
  const currentEnv = getEnvByHostname();
  const [swimlane, setSwimlane] = useState(localStorage.getItem('swimlane') || '');

  const handleSwimlaneChange = (value) => {
    setSwimlane(value || '');
    if (value) {
      localStorage.setItem('swimlane', value);
    } else {
      localStorage.removeItem('swimlane');
    }
  };

  const handleLogout = async () => {
    try {
      await userApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.clear();
    testApi.invalidateChannelCurrenciesCache();
    navigate('/login');
  };

  const handleMenuClick = ({ key }) => {
    if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'logout') {
      handleLogout();
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('personalSettings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('logout'),
    },
  ];

  const getSelectedKey = () => {
    if (location.pathname === '/dashboard') return 'dashboard';
    if (location.pathname === '/account-create') return 'account-create';
    if (location.pathname === '/payin') return 'payin';
    if (location.pathname === '/payout') return 'payout';
    if (location.pathname === '/query') return 'query';
    if (location.pathname === '/order') return 'order';
    if (location.pathname === '/withdraw') return 'withdraw';
    if (location.pathname === '/refund') return 'refund';
    if (location.pathname === '/refund-query') return 'refund-query';
    if (location.pathname === '/order-list') return 'order-list';
    if (location.pathname === '/bankcard') return 'bankcard';
    if (location.pathname === '/bankaccount-query') return 'bankaccount-query';
    if (location.pathname === '/history') return 'history';
    if (location.pathname === '/api-docs') return 'api-docs';
    if (location.pathname === '/users') return 'users';
    if (location.pathname === '/channel-accounts') return 'channel-accounts';
    if (location.pathname === '/mock-rules') return 'mock-rules';
    return 'dashboard';
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <SessionTimeout />
      <Header style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '0 32px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        position: 'fixed',
        width: '100%',
        zIndex: 1000
      }}>
        <div style={{ 
          color: 'white', 
          fontSize: 24, 
          fontWeight: 700,
          background: 'linear-gradient(45deg, #ffffff, #f0f0f0)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          🚀 {t('testPlatform')}
        </div>
        <Space size={16} style={{ color: 'white' }}>
          {currentEnv === 'TEST' && (
            <Select
              value={swimlane || undefined}
              onChange={handleSwimlaneChange}
              allowClear
              showSearch
              placeholder="🏊 Swimlane"
              style={{ width: 160 }}
              size="middle"
              popupMatchSelectWidth={false}
              optionFilterProp="label"
              dropdownStyle={{ maxHeight: 300 }}
              options={Array.from({ length: 30 }, (_, i) => {
                const val = `pfb${i + 1}`;
                const colors = ['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2'];
                const color = colors[i % colors.length];
                return {
                  label: val,
                  value: val,
                  emoji: (
                    <Space size={6}>
                      <span style={{
                        display: 'inline-block', width: 8, height: 8,
                        borderRadius: '50%', background: color
                      }} />
                      <span style={{ fontWeight: 500 }}>{val}</span>
                    </Space>
                  ),
                };
              })}
              optionRender={(option) => option.data.emoji}
              labelRender={({ value: v }) => (
                <Space size={4}>
                  <span style={{ fontSize: 12 }}>🏊</span>
                  <span style={{ fontWeight: 600, color: '#1677ff' }}>{v}</span>
                </Space>
              )}
            />
          )}
          <Select
            value={language}
            onChange={changeLanguage}
            style={{ width: 130 }}
            size="middle"
            suffixIcon={<GlobalOutlined style={{ color: '#667eea' }} />}
            options={[
              { label: '🇨🇳 中文', value: 'zh-CN' },
              { label: '🇺🇸 English', value: 'en-US' },
            ]}
          />
          <Dropdown menu={{ items: userMenuItems, onClick: handleMenuClick }} placement="bottomRight">
            <Space style={{ cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)' }}>
              <Avatar size={32} style={{ background: '#f56a00' }}>
                {username?.charAt(0)?.toUpperCase()}
              </Avatar>
              <span style={{ fontWeight: 500 }}>{username}</span>
            </Space>
          </Dropdown>
        </Space>
      </Header>
      
      <Layout style={{ marginTop: 64 }}>
        <Sider 
          width={220}
          style={{
            background: '#fff',
            boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
            position: 'fixed',
            left: 0,
            top: 64,
            bottom: 0,
            overflow: 'auto'
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[getSelectedKey()]}
            style={{ height: '100%', borderRight: 0, paddingTop: 16 }}
            items={[
              {
                key: 'dashboard',
                icon: <DashboardOutlined />,
                label: <Link to="/dashboard">{t('dashboard') || 'Dashboard'}</Link>,
              },
              {
                type: 'divider',
              },
              {
                key: 'account-create',
                icon: <UserAddOutlined />,
                label: <Link to="/account-create">{t('accountCreate')}</Link>,
              },
              {
                key: 'query',
                icon: <SearchOutlined />,
                label: <Link to="/query">{t('accountQuery')}</Link>,
              },
              {
                key: 'payin',
                icon: <DollarOutlined />,
                label: <Link to="/payin">{t('payin')}</Link>,
              },
              {
                key: 'order',
                icon: <ShoppingOutlined />,
                label: <Link to="/order">{t('payinQuery')}</Link>,
              },
              {
                key: 'bankcard',
                icon: <BankOutlined />,
                label: <Link to="/bankcard">{t('bankCardBind')}</Link>,
              },
              {
                key: 'bankaccount-query',
                icon: <SearchOutlined />,
                label: <Link to="/bankaccount-query">{t('bankAccountQuery')}</Link>,
              },
              {
                key: 'payout',
                icon: <WalletOutlined />,
                label: <Link to="/payout">{t('payout')}</Link>,
              },
              {
                key: 'withdraw',
                icon: <ExportOutlined />,
                label: <Link to="/withdraw">{t('payoutQuery')}</Link>,
              },
              {
                key: 'refund',
                icon: <RollbackOutlined />,
                label: <Link to="/refund">{t('refundPage')}</Link>,
              },
              {
                key: 'refund-query',
                icon: <SearchOutlined />,
                label: <Link to="/refund-query">{t('refundQuery')}</Link>,
              },
              {
                type: 'divider',
              },
              {
                key: 'order-list',
                icon: <UnorderedListOutlined />,
                label: <Link to="/order-list">{t('orderListQuery')}</Link>,
              },
              {
                key: 'history',
                icon: <HistoryOutlined />,
                label: <Link to="/history">{t('executionHistory')}</Link>,
              },
              {
                key: 'api-docs',
                icon: <FileTextOutlined />,
                label: <Link to="/api-docs">{t('doc_tabDoc') || 'API 文档'}</Link>,
              },
              ...(currentEnv !== 'PROD' ? [{
                key: 'mock-rules',
                icon: <ApiOutlined />,
                label: <Link to="/mock-rules">{t('mockRules')}</Link>,
              }] : []),
              ...(userRole === 'ADMIN' ? [
                {
                  type: 'divider',
                },
                {
                  key: 'users',
                  icon: <TeamOutlined />,
                  label: <Link to="/users">{t('userManagement')}</Link>,
                },
                {
                  key: 'channel-accounts',
                  icon: <BankOutlined />,
                  label: <Link to="/channel-accounts">{t('channelAccountManagement')}</Link>,
                }
              ] : [])
            ]}
          />
        </Sider>
        
        <Content style={{ marginLeft: 220, background: '#f5f7fa', minHeight: 'calc(100vh - 64px)' }}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/account-create" element={<AccountCreate />} />
            <Route path="/payin" element={<Payin />} />
            <Route path="/payout" element={<Payout />} />
            <Route path="/query" element={<AccountQuery />} />
            <Route path="/order" element={<OrderQuery />} />
            <Route path="/withdraw" element={<WithdrawQuery />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/refund-query" element={<RefundQuery />} />
            <Route path="/order-list" element={<OrderList />} />
            <Route path="/bankcard" element={<BankCardBind />} />
            <Route path="/bankaccount-query" element={<BankAccountQuery />} />
            <Route path="/history" element={<ExecutionHistory />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/channel-accounts" element={<ChannelAccountManagement />} />
            <Route path="/mock-rules" element={<MockRuleManagement />} />
            <Route path="/api-docs" element={<ApiDocumentation />} />
            <Route path="/profile" element={<UserProfile />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

import { LoginOutlined } from '@ant-design/icons';

function PublicApiDocs() {
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem('isLogin') === 'true';
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 32px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          color: 'white', fontSize: 22, fontWeight: 700,
          background: 'linear-gradient(45deg, #ffffff, #f0f0f0)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          📄 Fiat Payment API
        </div>
        <Space size={12}>
          <Select value={language} onChange={changeLanguage} style={{ width: 120 }} size="small"
            suffixIcon={<GlobalOutlined style={{ color: '#667eea' }} />}
            options={[{ label: '🇨🇳 中文', value: 'zh-CN' }, { label: '🇺🇸 English', value: 'en-US' }]} />
          {isLoggedIn ? (
            <Button type="primary" ghost size="small"
              onClick={() => navigate('/')} style={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff' }}>
              🚀 {t('backToConsole') || '返回控制台'}
            </Button>
          ) : (
            <Button type="primary" ghost size="small" icon={<LoginOutlined />}
              onClick={() => navigate('/login')} style={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff' }}>
              {t('login')}
            </Button>
          )}
        </Space>
      </Header>
      <Content style={{ background: '#f5f7fa', padding: 0 }}>
        <ApiDocumentation isPublic={!isLoggedIn} />
      </Content>
    </Layout>
  );
}

function App() {
  return (
    <PageCacheProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/api-docs" element={<PublicApiDocs />} />
          <Route path="/*" element={<PrivateRoute><AppLayout /></PrivateRoute>} />
        </Routes>
      </BrowserRouter>
    </PageCacheProvider>
  );
}

export default App;
